import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Filter,
  Calendar,
  User,
  Play,
  Pause,
  RotateCcw,
  Trash2,
  MoreVertical,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-emerald-500" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'running':
      return <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />;
    case 'pending':
      return <Clock className="h-4 w-4 text-amber-500" />;
    default:
      return <AlertCircle className="h-4 w-4 text-slate-400" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'bg-emerald-100 text-emerald-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    case 'running':
      return 'bg-blue-100 text-blue-700';
    case 'pending':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

const BulkActionsTab = ({
  token,
  agencyId = null,
  isAgency = false,
}) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [operationFilter, setOperationFilter] = useState('all');

  useEffect(() => {
    fetchBulkJobs();
  }, [token, agencyId]);

  const fetchBulkJobs = async () => {
    setLoading(true);
    try {
      const endpoint = isAgency
        ? `${API}/agency/opportunities/bulk-jobs`
        : `${API}/platform/opportunities/bulk-jobs`;

      const headers = {
        Authorization: `Bearer ${token}`,
      };
      if (agencyId) {
        headers['X-Agency-ID'] = agencyId;
      }

      const response = await axios.get(endpoint, { headers });
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch bulk jobs:', err);
      // Show empty state on error
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter((job) => {
    if (statusFilter !== 'all' && job.status !== statusFilter) return false;
    if (operationFilter !== 'all' && job.operation !== operationFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bulk-actions-tab">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Select value={operationFilter} onValueChange={setOperationFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Operation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Operations</SelectItem>
            <SelectItem value="move_stage">Move Stage</SelectItem>
            <SelectItem value="assign_owner">Assign Owner</SelectItem>
            <SelectItem value="add_tags">Add Tags</SelectItem>
            <SelectItem value="remove_tags">Remove Tags</SelectItem>
            <SelectItem value="export">Export</SelectItem>
            <SelectItem value="archive">Archive</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <Button variant="outline" size="sm" onClick={fetchBulkJobs}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Jobs Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-600 uppercase tracking-wider">
          <div className="col-span-3">Action</div>
          <div className="col-span-2">Operation</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">User</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-1">Stats</div>
          <div className="col-span-1">Actions</div>
        </div>

        {/* Table Body */}
        {filteredJobs.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Clock className="mx-auto h-10 w-10 text-slate-300 mb-3" />
            <p className="font-medium">No bulk actions found</p>
            <p className="text-sm mt-1">
              Bulk actions will appear here when you perform operations on multiple opportunities
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-50 transition-colors"
                data-testid={`bulk-job-row-${job.id}`}
              >
                {/* Action Label */}
                <div className="col-span-3">
                  <p className="font-medium text-stone-900 truncate">
                    {job.title || `Bulk ${job.operation}`}
                  </p>
                  {job.filters && Object.keys(job.filters).length > 0 && (
                    <p className="text-xs text-slate-500 truncate">
                      {JSON.stringify(job.filters)}
                    </p>
                  )}
                </div>

                {/* Operation */}
                <div className="col-span-2">
                  <Badge variant="outline" className="text-xs capitalize">
                    {job.operation?.replace(/_/g, ' ') || 'Unknown'}
                  </Badge>
                </div>

                {/* Status */}
                <div className="col-span-1 flex items-center gap-2">
                  {getStatusIcon(job.status)}
                  <Badge className={`text-xs ${getStatusColor(job.status)}`}>
                    {job.status}
                  </Badge>
                </div>

                {/* User */}
                <div className="col-span-2 flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600 truncate">
                    {job.requested_by || 'System'}
                  </span>
                </div>

                {/* Created */}
                <div className="col-span-2 flex items-center gap-2 text-sm text-slate-500">
                  <Calendar className="h-4 w-4" />
                  {formatDate(job.created_at)}
                </div>

                {/* Stats */}
                <div className="col-span-1 text-sm">
                  {job.status === 'completed' ? (
                    <span className="text-emerald-600">
                      {job.success_count || 0}/{job.affected_count || 0}
                    </span>
                  ) : job.status === 'failed' ? (
                    <span className="text-red-600">
                      {job.failed_count || 0} failed
                    </span>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </div>

                {/* Actions */}
                <div className="col-span-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        View Details
                      </DropdownMenuItem>
                      {job.status === 'running' && (
                        <DropdownMenuItem>
                          <Pause className="mr-2 h-4 w-4" />
                          Pause
                        </DropdownMenuItem>
                      )}
                      {job.status === 'failed' && (
                        <DropdownMenuItem>
                          <Play className="mr-2 h-4 w-4" />
                          Retry
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem className="text-red-600">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkActionsTab;
